import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { FileInfo, FileUploadError, GALAXY_UPLOADER_SERVICE_TOKEN } from '@vendasta/galaxy/uploader';
import { GalaxyImageUploaderService } from '@vendasta/galaxy/uploader/src/image-uploader.service';
import { SelectInputOption } from '@vendasta/galaxy/input';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-image-uploader-editor',
  templateUrl: './image-uploader-editor.component.html',
  styleUrls: ['./image-uploader-editor.component.scss'],
  providers: [{ provide: GALAXY_UPLOADER_SERVICE_TOKEN, useClass: GalaxyImageUploaderService }],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ImageUploaderEditorComponent implements OnInit, OnDestroy {
  @Input() uploadUrl: string;
  @Input() aspects: SelectInputOption[];
  @Input() currentImageUrl: string;
  @Output() uploadedFile: EventEmitter<FileInfo> = new EventEmitter<FileInfo>();
  files: FileInfo[] = [];
  currentFile: FileInfo;
  maxDimensions = { width: 1200, height: 1200 };
  maxEditorHeight = '250px';
  showUploadButton = true;
  private subsciptions: Subscription[] = [];

  constructor(
    private snack: SnackbarService,
    @Inject(GALAXY_UPLOADER_SERVICE_TOKEN) private uploadService: GalaxyImageUploaderService,
  ) {}

  ngOnInit(): void {
    this.subsciptions.push(
      this.uploadService.fileUploaded$.pipe(filter((fileInfo) => !!fileInfo)).subscribe({
        next: (fileInfo: FileInfo) => this.onUpload(fileInfo),
      }),
    );
    this.subsciptions.push(
      this.uploadService.fileErrored$$.pipe(filter((error) => !!error)).subscribe({
        next: (error: FileUploadError) => this.onError(error),
      }),
    );
  }

  onError(event: FileUploadError): void {
    this.snack.errorSnack(event.error.message);
    this.showUploadButton = true;
  }

  onUpload(event: FileInfo): void {
    this.snack.successSnack(`Successfully uploaded: ${event.name}`);
    this.uploadedFile.emit(event);
    this.onFilesChanged([]);
    this.showUploadButton = false;
  }

  onFilesChanged(files: FileInfo[]): void {
    this.files = files;
  }

  editImage(info: FileInfo): void {
    this.currentFile = info;
  }

  endEdit(blob?: Blob): void {
    this.uploadService.setCroppedImage(this.currentFile, blob);
    this.currentFile = undefined;
    this.showUploadButton = true;
  }

  deleteImage(): void {
    if (this.files[0]) {
      this.uploadService.removeFile(this.files[0]);
      this.onFilesChanged([]);
    }
    this.uploadedFile.emit({ name: null, url: null });
    this.showUploadButton = true;
  }

  uploadFiles(): void {
    this.uploadService.uploadQueuedFiles();
  }

  ngOnDestroy(): void {
    this.subsciptions.forEach((sub: Subscription) => sub.unsubscribe());
  }
}
